/**
 * Validated environment access.
 *
 * Every `import.meta.env` read in the application funnels through here so that
 * a missing or malformed variable fails loudly at module-evaluation time rather
 * than surfacing as `undefined` deep inside a component.
 *
 * Only `VITE_`-prefixed values exist in the browser bundle. There is no code
 * path in this repository that can read a server secret.
 */

type RawEnv = Record<string, string | boolean | undefined>;

function rawEnv(): RawEnv {
  // `import.meta.env` is injected by Vite. In a plain Node context (unit tests
  // executed outside the Vite transform) fall back to `process.env` so the
  // module remains importable.
  const meta = (import.meta as unknown as { env?: RawEnv }).env;
  if (meta) return meta;
  const proc = (globalThis as { process?: { env?: RawEnv } }).process;
  return proc?.env ?? {};
}

const errors: string[] = [];

function readString(key: string, fallback?: string): string {
  const value = rawEnv()[key];
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  if (fallback !== undefined) return fallback;
  errors.push(`${key} is required but was not set.`);
  return '';
}

function readOptionalString(key: string): string | null {
  const value = rawEnv()[key];
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  return null;
}

function readBoolean(key: string, fallback: boolean): boolean {
  const value = rawEnv()[key];
  if (value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  const normalised = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(normalised)) return true;
  if (['false', '0', 'no', 'off'].includes(normalised)) return false;
  errors.push(`${key} must be a boolean-like value, received "${String(value)}".`);
  return fallback;
}

/** Strips any trailing slash so path joins never produce a double slash. */
function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

/** Guarantees a single leading slash and no trailing slash. */
function normaliseBasePath(value: string): string {
  const trimmed = stripTrailingSlash(value.trim());
  if (trimmed === '' || trimmed === '/') return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

const MODE = readString('MODE', 'production');
const IS_PRODUCTION_BUILD = readBoolean('PROD', MODE === 'production');

/**
 * A build intended to be served to real users, as distinct from one that merely
 * has production semantics.
 *
 * The end-to-end suite deliberately runs against a *built* bundle - that is the
 * only way base-path handling, chunk boundaries and prerendered HTML get tested -
 * but it must point at the local mock. `vite preview` cannot change that after
 * the fact, because Vite inlines `import.meta.env` at build time, so the e2e
 * build has to be its own mode. Everything that guards a deployable artefact
 * keys off this rather than off `PROD`, which is true for any build.
 */
const IS_DEPLOYABLE_BUILD = IS_PRODUCTION_BUILD && MODE !== 'e2e';

const env = {
  mode: MODE,
  isProductionBuild: IS_PRODUCTION_BUILD,
  isDevelopment: !IS_PRODUCTION_BUILD,

  appSlug: readString('VITE_APP_SLUG', 'ircf'),
  appName: readString('VITE_APP_NAME', 'Iron Resolve Combat Fitness'),

  /**
   * No default in a production build: a deployable bundle must be told where the
   * API is, and being told wrongly is worse than failing to build. In development
   * the default is the local mock, so a developer who has not copied
   * `.env.local.example` gets a working local stack rather than a browser quietly
   * firing requests at the live platform host and reporting empty pages.
   */
  apiBaseUrl: stripTrailingSlash(
    IS_DEPLOYABLE_BUILD
      ? readString('VITE_TAME_API_URL')
      : readString('VITE_TAME_API_URL', 'http://localhost:4010/api/v1'),
  ),
  basePath: normaliseBasePath(readString('VITE_APP_BASE_PATH', '/')),
  publicSiteUrl: stripTrailingSlash(
    IS_DEPLOYABLE_BUILD
      ? readString('VITE_PUBLIC_SITE_URL')
      : readString('VITE_PUBLIC_SITE_URL', 'http://localhost:5173'),
  ),

  timezone: readString('VITE_DEFAULT_TIMEZONE', 'Australia/Sydney'),
  currency: readString('VITE_DEFAULT_CURRENCY', 'AUD'),
  locale: readString('VITE_DEFAULT_LOCALE', 'en-AU'),

  enable3d: readBoolean('VITE_ENABLE_3D', true),
  enablePayments: readBoolean('VITE_ENABLE_PAYMENTS', false),
  enableExternalCalendar: readBoolean('VITE_ENABLE_EXTERNAL_CALENDAR', false),
  enableMfa: readBoolean('VITE_ENABLE_MFA', false),
  enableWaitlist: readBoolean('VITE_ENABLE_WAITLIST', true),
  enablePortal3dAccents: readBoolean('VITE_ENABLE_PORTAL_3D_ACCENTS', true),
  /** Installable PWA prompt on mobile. Off until the manifest/SW are served. */
  enablePwa: readBoolean('VITE_ENABLE_PWA', true),
  /**
   * Web Push subscription UI. Requires a VAPID public key. The private key stays
   * on the platform - never in this SPA.
   */
  enablePushNotifications: readBoolean('VITE_ENABLE_PUSH', true),
  /** URL-safe base64 VAPID public key for PushManager.subscribe. */
  vapidPublicKey: readOptionalString('VITE_VAPID_PUBLIC_KEY'),

  analyticsEnabled: readBoolean('VITE_ANALYTICS_ENABLED', false),
  analyticsEndpoint: readOptionalString('VITE_ANALYTICS_ENDPOINT'),

  debugPerformanceOverlay: readBoolean('VITE_DEBUG_PERFORMANCE_OVERLAY', false),
} as const;

// --- Cross-field integrity checks -------------------------------------------

const LOCALHOST_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:|\/|$)/i;

if (IS_DEPLOYABLE_BUILD && LOCALHOST_PATTERN.test(env.apiBaseUrl)) {
  errors.push(
    `VITE_TAME_API_URL points at a local address ("${env.apiBaseUrl}") in a production build. ` +
      'The production bundle must target the platform API. Refusing to build against the dev mock. ' +
      'If this is the end-to-end build, run it with `--mode e2e`.',
  );
}

if (env.analyticsEnabled && env.analyticsEndpoint === null) {
  errors.push('VITE_ANALYTICS_ENABLED is true but VITE_ANALYTICS_ENDPOINT is not set.');
}

if (env.debugPerformanceOverlay && IS_DEPLOYABLE_BUILD) {
  errors.push('VITE_DEBUG_PERFORMANCE_OVERLAY must not be enabled in a production build.');
}

if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(env.appSlug)) {
  errors.push(
    `VITE_APP_SLUG must be a lowercase kebab-case slug, received "${env.appSlug}". ` +
      'It is used verbatim in the API namespace, base path and database table prefix.',
  );
}

if (errors.length > 0) {
  throw new Error(
    `Invalid environment configuration:\n${errors.map((message) => `  - ${message}`).join('\n')}\n\n` +
      'See .env.example for the full documented variable set.',
  );
}

export type Env = typeof env;
export { env };
